import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Note } from '@card-software/library';
import { ApiResponse } from '@card-software/nexus-utilities';
import { ConsumptionWarehouseLocation, RecipePreviewItem } from '@app/models/dyeing-dashboard/production-card-dyeing-cycle-recipe-values';
import { Roll } from '@app/models/knitting/roll';
import { SecondQualityRollCommand } from '@app/models/production/commands/second-quality-roll-command';
import { ConsumedItem, FinishingRoute } from '@app/models/production/finishing-route';
import { FinishingRouteProductionCard } from '@app/models/production/finishing-route-production-card';
import { getSkipperErrorHeader } from '@app/utilities/error-header.util';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FinishingRouteProxyService {
  private readonly httpClient = inject(HttpClient);
  private baseUrl = `${environment.proxyUrls.productionApi}/api/v1/finishing-route`;

  public getById(id: string): Observable<ApiResponse<FinishingRoute>> {
    return this.httpClient.get<ApiResponse<FinishingRoute>>(`${this.baseUrl}/${id}`);
  }

  public getByNumber(number: string): Observable<ApiResponse<FinishingRoute>> {
    return this.httpClient.get<ApiResponse<FinishingRoute>>(`${this.baseUrl}/number/${encodeURIComponent(number)}`);
  }

  public getByRollNumber(rollNumber: string): Observable<ApiResponse<FinishingRoute>> {
    const headers = getSkipperErrorHeader(true);
    return this.httpClient.get<ApiResponse<FinishingRoute>>(`${this.baseUrl}/by-roll-number/${encodeURIComponent(rollNumber)}`, {
      headers,
    });
  }

  public getByProductionCardId(productionCardId: string): Observable<ApiResponse<FinishingRoute[]>> {
    const headers = getSkipperErrorHeader(true);
    return this.httpClient.get<ApiResponse<FinishingRoute[]>>(`${this.baseUrl}/production-card/${productionCardId}`, { headers });
  }

  public updateStationAction(finishingRouteNumber: string, stationAssetId: string): Observable<ApiResponse<FinishingRoute>> {
    return this.httpClient.patch<ApiResponse<FinishingRoute>>(
      `${this.baseUrl}/number/${encodeURIComponent(finishingRouteNumber)}/station-action`,
      null,
      {
        params: { stationAssetId },
      },
    );
  }

  validateStatusRouteOfPC(idProductionCard: string): Observable<ApiResponse<FinishingRouteProductionCard[]>> {
    return this.httpClient.get<ApiResponse<FinishingRouteProductionCard[]>>(`${this.baseUrl}/production-card/${idProductionCard}`);
  }

  getRoutesOfPC(PCFinishingRoute: string): Observable<ApiResponse<FinishingRouteProductionCard>> {
    return this.httpClient.get<ApiResponse<FinishingRouteProductionCard>>(`${this.baseUrl}/number/${encodeURIComponent(PCFinishingRoute)}`);
  }

  startActionFinishingRoute(stationID: string, finishingRouteNumber: string): Observable<ApiResponse<FinishingRouteProductionCard>> {
    return this.httpClient.patch<ApiResponse<FinishingRouteProductionCard>>(
      `${this.baseUrl}/number/${encodeURIComponent(finishingRouteNumber)}/station-action`,
      null,
      {
        params: { stationAssetId: stationID },
      },
    );
  }

  public updateRoute(id: string, newRoute: string[]): Observable<ApiResponse<FinishingRoute>> {
    return this.httpClient.patch<ApiResponse<FinishingRoute>>(`${this.baseUrl}/${id}/update-route`, newRoute);
  }

  public updateNotes(id: string, notes: Note[]): Observable<ApiResponse<FinishingRoute>> {
    return this.httpClient.patch<ApiResponse<FinishingRoute>>(`${this.baseUrl}/${id}/notes`, notes);
  }
  public printFinishingReport(finishingRouteId: string): Observable<Blob> {
    const url = `${this.baseUrl}/${finishingRouteId}/generate-report`;
    return this.httpClient.post(url, null, { responseType: 'blob' });
  }

  public addConsumedItems(
    finishingRouteId: string,
    lineId: string,
    consumedItems: ConsumedItem[],
  ): Observable<ApiResponse<FinishingRoute>> {
    return this.httpClient.patch<ApiResponse<FinishingRoute>>(
      `${this.baseUrl}/${finishingRouteId}/process/${lineId}/consumed-items`,
      consumedItems,
    );
  }

  public completeConsumedItems(finishingRouteId: string, lineId: string): Observable<ApiResponse<FinishingRoute>> {
    return this.httpClient.patch<ApiResponse<FinishingRoute>>(
      `${this.baseUrl}/${finishingRouteId}/process/${lineId}/consumed-items/complete`,
      {},
    );
  }

  public getConsumedItemsEnrichment(itemCodes: string[]): Observable<ApiResponse<RecipePreviewItem[]>> {
    return this.httpClient.post<ApiResponse<RecipePreviewItem[]>>(`${this.baseUrl}/consumed-items/enrichment`, itemCodes);
  }

  public getConsumptionWarehouse(): Observable<ApiResponse<ConsumptionWarehouseLocation | null>> {
    return this.httpClient.get<ApiResponse<ConsumptionWarehouseLocation | null>>(`${this.baseUrl}/consumed-items/consumption-warehouse`);
  }

  public addSecondQualityRolls(
    finishingRouteId: string,
    secondQualityRollCommand: SecondQualityRollCommand,
  ): Observable<ApiResponse<Roll>> {
    return this.httpClient.post<ApiResponse<Roll>>(
      `${this.baseUrl}/${finishingRouteId}/roll/create-second-quality`,
      secondQualityRollCommand,
    );
  }
}
